import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import Heading from '@tds/core-heading';

import Filters from '../filters/index';
import { Button, ButtonWrapper } from './css';
import { ContentWrapper, Box } from '../design/css';
import Table from '../design/shared/table';
import request from '../utils/request';
import { OrderListModel, UsersListModel, getOptions } from './models';
import OrderDetails from '../orderDetails';
import ExportButton from './exportButton';
import MessageNotification from '../messageNotification';
import ValidateLoader from '../UI/validateLoader';
import EN from '../../EN.json';
import FR from '../../FR.json';

const locale = localStorage.getItem('locale');
const staticLabels = locale === 'FR' ? FR : EN;
const {
  ORDERS: {
    titleOrders,
    viewOrder,
    EXPORT,
    COMP_EXPORT,
    NO_ORDERS,
    LOADING_MESSAGE,
    ORDER_WITH,
    SUCCESS_MESSAGE
  },
  general: { loading: loadingLabel }
} = staticLabels;

// @Todo move headers from API to FE translations
const ordersHeaders = [
  'order_id',
  'value_chain',
  'createdAt',
  'status',
  's1_sw_lead_source_company',
  's2_customerinformation_legal_business_name',
  's2_channelrep_agent_name',
  'completedAt'
];

const getHeaders = headers => {
  const orderAssignedTo = locale === 'FR' ? 'Responsable' : 'Assignee';
  const actions = locale === 'FR' ? 'Actions' : 'Actions';
  const result = ordersHeaders.reduce(
    (acc, i) => ({ ...acc, [i]: headers[i] }),
    {}
  );

  return { ...result, orderAssignedTo, actions };
};

const fetchOrders = page => {
  let url = '/orders';
  if (page) url += `?page=${page}`;
  return request.get(url);
};

const fetchOrder = id => {
  return request.get(`/orders/${id}`);
};

const fetchFilteredOrders = payload => {
  return request.post('/orders/filter', payload);
};

const fetchUsers = () => {
  return request.get('/users/list');
};

const Orders = ({ history, location: { search } }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [filterPayload, setFilterPayload] = useState();
  const [orders, setOrders] = useState(null);
  const [totalOrders, setTotalOrders] = useState(null);
  const [headers, setHeaders] = useState(null);
  const [orderDetails, setOrderDetails] = useState();
  const [updatedOrder, setOrderUpdated] = useState();
  const [users, setUsers] = useState();
  const { page } = queryString.parse(search);
  const isOrderUpdated = updatedOrder && updatedOrder.isOrderUpdated;
  const updateOrderId = updatedOrder && updatedOrder.orderId;

  const getOrders = () => {
    setIsLoading(true);
    fetchOrders(page).then(({ data }) => {
      setOrders(data.orders);
      setTotalOrders(data.count);
      setHeaders(getHeaders(data.headers));
      setIsLoading(false);
    });
  };

  const filterOrders = (payload = {}) => {
    setIsLoading(true);
    fetchFilteredOrders({ ...payload, page }).then(({ data }) => {
      setOrders(data.orders);
      setTotalOrders(data.count);
      setHeaders(getHeaders(data.headers));
      setOrderUpdated({ ...updatedOrder, isOrderUpdated: false });
      setIsLoading(false);
    });
  };

  useEffect(() => {
    fetchUsers().then(({ data }) =>
      setUsers(getOptions(data.users, UsersListModel))
    );
  }, []);

  useEffect(() => {
    if (!orderDetails && orderDetails !== null) {
      if (filterPayload) {
        filterOrders(filterPayload);
      } else {
        getOrders();
      }
    }
  }, [orderDetails, page]);

  const onClickView = id => {
    setIsLoading(true);
    fetchOrder(id).then(({ data }) => {
      setOrderDetails({ order: data.orders, headers: data.headers });
      setOrderUpdated({ ...updatedOrder, isOrderUpdated: false });
      setIsLoading(false);
    });
  };

  const onSave = value => {
    setOrderUpdated(value);
  };

  const getActions = row => (
    <Button onClick={() => onClickView(row.id)}>{viewOrder}</Button>
  );

  // useEffect(() => {
  //   if (isOrderUpdated) {
  //     setOrderUpdated({ ...updatedOrder, isOrderUpdated: false });
  //   }
  // }, [startDate, endDate, filterStatus]);

  return (
    <ContentWrapper>
      <Filters
        history={history}
        filterOrders={filterOrders}
        setFilterPayload={setFilterPayload}
        page={page}
        users={users}
      />
      {isOrderUpdated && updateOrderId && (
        <Box pt={20} pb={20}>
          <MessageNotification
            variant="success"
            message={`${ORDER_WITH}${updateOrderId}${SUCCESS_MESSAGE}`}
          />
        </Box>
      )}
      {isLoading ? (
        <ValidateLoader message={LOADING_MESSAGE} />
      ) : (
        <>
          {totalOrders ? (
            <>
              <Table
                title={titleOrders}
                headers={headers}
                data={orders}
                actions={getActions}
                dataModel={OrderListModel}
                currentPage={parseInt(page || 1, 10)}
                totalRecords={totalOrders}
              />
              <ButtonWrapper>
                <ExportButton
                  exportType="export"
                  label={EXPORT}
                  loadingLabel={loadingLabel}
                  filterPayload={filterPayload}
                />
                <div>&nbsp;</div>
                <ExportButton
                  exportType="comp"
                  label={COMP_EXPORT}
                  loadingLabel={loadingLabel}
                  filterPayload={filterPayload}
                />
              </ButtonWrapper>
            </>
          ) : (
            <Heading level="h3">{NO_ORDERS}</Heading>
          )}
        </>
      )}
      {orderDetails && (
        <OrderDetails
          open={!!orderDetails}
          setOpen={setOrderDetails}
          order={orderDetails.order}
          labels={orderDetails.headers}
          onSave={onSave}
          staticLabels={staticLabels.ORDERS}
          users={users}
        />
      )}
    </ContentWrapper>
  );
};

Orders.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string
  }).isRequired
};

export default Orders;
